import type { WritableDeep } from 'type-fest';

import { ensureDefined } from '@e-socna/utils';
import type { Id, OrientationEnum, PaperFormatEnum } from '@e-socna/data-layer';

import { getDeepSection } from '../../utils/get-deep-section.util';
import type { Editable } from '../../types/editable.type';

import type { ReducerContext } from '../types/reducer-context.type';

export interface CustomLayoutUpdates {
  readonly hasPagebreakBefore: boolean;
  readonly hasPagebreakAfter: boolean;
  readonly orientation: OrientationEnum;
  readonly paperFormat: PaperFormatEnum;
}

interface UpdateCustomLayoutParams<E extends Editable> {
  readonly clonedEditable: WritableDeep<E>;
  readonly insertionPointRealPath: readonly number[];
  readonly sectionId: Id;
  readonly updates: Partial<CustomLayoutUpdates>;
  readonly context: ReducerContext<E>;
}

export const updateCustomLayout = <E extends Editable>({
  clonedEditable,
  insertionPointRealPath,
  sectionId,
  updates,
  context,
}: Readonly<UpdateCustomLayoutParams<E>>): boolean => {
  const insertionPoint = getDeepSection(
    context.getEditable<true>(clonedEditable).virtualRoot,
    insertionPointRealPath,
  );

  const existingIndex =
    insertionPoint.customLayouts?.findIndex(
      (layout) => layout.sectionId === sectionId,
    ) ?? -1;

  if (existingIndex > -1) {
    const { customLayouts } = ensureDefined({
      customLayouts: insertionPoint.customLayouts,
    });

    const existing = customLayouts[existingIndex];

    if (!existing) {
      throw new Error('Custom layout index found but element is undefined');
    }

    // Check if any value actually changes
    const hasChanges = Object.entries(updates).some(
      ([key, value]) => existing[key as keyof typeof existing] !== value,
    );

    if (!hasChanges) {
      return false; // No update needed
    }

    Object.assign(existing, updates);
  } else {
    const newLayout = {
      __typename: 'CustomLayout' as const,
      _id: context.generateId(),
      sectionId,
      hasPagebreakBefore: updates.hasPagebreakBefore ?? null,
      hasPagebreakAfter: updates.hasPagebreakAfter ?? null,
      orientation: updates.orientation ?? null,
      paperFormat: updates.paperFormat ?? null,
    };

    if (insertionPoint.customLayouts?.length) {
      insertionPoint.customLayouts.push(newLayout);
    } else {
      insertionPoint.customLayouts = [newLayout];
    }
  }

  return true;
};
